import { sortBy } from "lodash";
import React, { FC, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { getSanitizedHtmlFromAllTags } from "../../../../../utils/get-sanitized-html-from-all-tags";
import { getTechSheetFieldSlugifiedName } from "../../../../../utils/products";
import { Checkbox, FormFieldLabel, Pill } from "../../../../forms";
import { TagColors, TagDimensions } from "../../../../forms/elements/tags/types";
import { ProductFinderFiltersFormTypes } from "../../types";

export interface MultiSelectFieldFilterProps extends Queries.TechSheetFieldDataFragment {
  variant?: "checkbox" | "pill";
}

export const MultiSelectFieldFilter: FC<MultiSelectFieldFilterProps> = ({
  allowedValues,
  internalName,
  label,
  variant,
}) => {
  const fieldName = getTechSheetFieldSlugifiedName(internalName ?? "");
  const { watch, setValue } = useFormContext<ProductFinderFiltersFormTypes>();
  const multiValue = watch(fieldName);
  const multiValueAsArray = useMemo(
    () =>
      typeof multiValue === "undefined"
        ? []
        : typeof multiValue === "string"
        ? [multiValue]
        : multiValue,
    [multiValue]
  );

  const toggleValue = useCallback(
    (singleValue: Queries.AllowedValueDataFragment) => {
      let newMultiValue = Array.from(multiValueAsArray);
      if (singleValue.internalName) {
        if (multiValueAsArray.includes(singleValue.internalName)) {
          newMultiValue = newMultiValue.filter((v) => v && v !== singleValue.internalName);
        } else {
          newMultiValue = [...newMultiValue, singleValue.internalName].filter(Boolean);
        }
      }
      setValue(fieldName, newMultiValue);
    },
    [multiValueAsArray, setValue, fieldName]
  );
  const orderedAllowedValues = useMemo(
    () => sortBy(allowedValues, (value) => value?.value?.toLowerCase()),
    [allowedValues]
  );
  const allowedValuesCount = allowedValues?.length ?? 0;
  if (!allowedValuesCount) {
    return null;
  }
  return (
    <div className="flex-grow flex-shrink-0">
      <FormFieldLabel label={label} />

      {variant === "pill" || (!variant && allowedValuesCount <= 3) ? (
        <div className="flex w-full items-center gap-2 flex-wrap">
          {orderedAllowedValues?.map((allowedValue) => {
            const label = getSanitizedHtmlFromAllTags(allowedValue?.value);
            return (
              allowedValue && (
                <Pill
                  color={TagColors.Primary}
                  key={allowedValue?.internalName}
                  dimension={TagDimensions.Medium}
                  className="font-medium"
                  checked={Boolean(
                    allowedValue.internalName &&
                      multiValueAsArray.includes(allowedValue.internalName)
                  )}
                  onChange={() => toggleValue(allowedValue)}
                >
                  {label}
                </Pill>
              )
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-x-4 gap-y-1">
          {orderedAllowedValues?.map((allowedValue) => {
            const label = getSanitizedHtmlFromAllTags(allowedValue?.value);
            return (
              allowedValue && (
                <Checkbox
                  checkboxAlignment="top"
                  color={TagColors.Primary}
                  key={allowedValue?.internalName}
                  collapseMessage
                  className="font-medium col-span-12 sm:col-span-6"
                  checked={Boolean(
                    allowedValue.internalName &&
                      multiValueAsArray.includes(allowedValue.internalName)
                  )}
                  onChange={() => toggleValue(allowedValue)}
                >
                  {label}
                </Checkbox>
              )
            );
          })}
        </div>
      )}
    </div>
  );
};
